import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { SubstitutionDay } from "../types";
import { fetchSubstitutionDay, saveSubstitutionDay } from "../services/substitutionService";

// The backend sends clash reasons (e.g. "Chitra teaches Seven - A then (P1, Eight - A)") as plain text.
const messageOf = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  return typeof data === "string" && data.trim() ? data : fallback;
};

type Picks = Record<number, number | null>; // timetableSlotId → substitute faculty id (null = not covered)

const picksOf = (day: SubstitutionDay | null): Picks =>
  Object.fromEntries((day?.needs ?? []).map((need) => [need.timetableSlotId, need.substituteFacultyId]));

/**
 * One date's periods needing cover. Picks stay in `draft` until `save` (all or nothing); switching
 * date with unsaved picks goes through `pendingDate`.
 */
export const useSubstitutionDay = () => {
  const [date, setDate] = useState(() => dayjs().format("YYYY-MM-DD"));
  const [pendingDate, setPendingDate] = useState<string | null>(null);
  const [day, setDay] = useState<SubstitutionDay | null>(null);
  const [draft, setDraft] = useState<Picks>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSaveError(null);
    fetchSubstitutionDay(date)
      .then((data) => {
        if (cancelled) return;
        setDay(data);
        setDraft(picksOf(data));
      })
      .catch((err) => !cancelled && setError(messageOf(err, "Failed to load substitutions.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [date]);

  const saved = useMemo(() => picksOf(day), [day]);
  const changedSlots = useMemo(
    () => Object.keys(draft).map(Number).filter((slotId) => (draft[slotId] ?? null) !== (saved[slotId] ?? null)),
    [draft, saved]
  );
  const dirty = changedSlots.length > 0;

  const pick = (slotId: number, facultyId: number | null) => {
    setSaveError(null);
    setDraft((prev) => ({ ...prev, [slotId]: facultyId }));
  };

  const discard = () => {
    setDraft(saved);
    setSaveError(null);
  };

  /** Returns true when saved; on failure the picks stay on screen and `saveError` says why. */
  const save = async (): Promise<boolean> => {
    setSaving(true);
    setSaveError(null);
    try {
      const result = await saveSubstitutionDay(
        date,
        changedSlots.map((slotId) => ({ timetableSlotId: slotId, substituteFacultyId: draft[slotId] ?? null }))
      );
      setDay(result);
      setDraft(picksOf(result));
      return true;
    } catch (err) {
      setSaveError(messageOf(err, "Failed to save substitutions. Your picks are still on screen."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  /** Changes date at once, or asks first (pendingDate) when there are unsaved picks. */
  const selectDate = (next: string) => {
    if (next === date) return;
    if (dirty) setPendingDate(next);
    else setDate(next);
  };
  const confirmDate = () => {
    if (pendingDate) setDate(pendingDate);
    setPendingDate(null);
  };
  const cancelDate = () => setPendingDate(null);

  return {
    date,
    pendingDate,
    day,
    draft,
    changedSlots,
    dirty,
    loading,
    saving,
    error,
    saveError,
    actions: { selectDate, confirmDate, cancelDate, pick, discard, save },
  };
};
